import type { Recording } from 'core'
import { mountVNode, sanitizeObject, transcriptionToVTT } from 'core'
import { useAudioPlayer } from './audio-player'
import { getData, useRecordingState } from './data'

export namespace Transcription {
  /** Assigns index as id to transcription sentences */
  export function fromRawContent(source: Recording.ITranscription) {
    let lineIndex = -1

    return Array.from(source).map((paragraph, pIndex) =>
      ({
        ...paragraph,
        index: pIndex,
        sentences: paragraph.sentences.map((item) => {
          return { ...item, id: ++lineIndex }
        })
      })
    )
  }

  export type IContent = ReturnType<typeof fromRawContent>
  export type IContentParagraph = IContent[0]
  export type IContentSentence = IContentParagraph['sentences'][0]
  export type IStatus = Record<Recording['transcriptionStatus'], boolean>
}

function useCaptionsLoader(content: Ref<Transcription.IContent>) {
  const audioRef = useAudioPlayer()
  const activeTrack = ref<number>()

  const trackID = useId()
  const track = h('track', {
    id: trackID,
    srclang: 'en',
    default: true,
    kind: 'captions',
    onCuechange: onCueChange,
    onLoad: onTrackLoaded,
    onVnodeBeforeUnmount(vnode) {
      URL.revokeObjectURL(vnode.props!.src)
    }
  })

  const isValid = () => content.value.length > 0

  function getTextTrack() {
    const track = audioRef.audio.textTracks.getTrackById(trackID)

    if (!track)
      throw new Error(`No text track found with id - ${trackID}`)

    const cues = Array.from(track.cues || [])
    const getCue = (id: number | string) => track.cues?.getCueById(String(id))

    return { track, cues, getCue }
  }

  function onTrackLoaded() {
    const { cues } = getTextTrack()

    // Assign index as id to cues
    cues.forEach((cue, index) => cue.id = String(index))
    console.debug('Captions track has loaded', { cues })
  }

  function onCueChange() {
    const { track } = getTextTrack()
    const cue = track.activeCues?.[0]

    if (cue)
      activeTrack.value = +cue.id
  }

  function load() {
    const file = new Blob(
      [transcriptionToVTT(content.value)],
      { type: 'text/vtt' }
    )

    // Update track src
    track.props ||= {}
    track.props.src = URL.createObjectURL(file)

    mountVNode(track, { element: audioRef.audio })
    console.debug('Captions file mounted')
  }

  onMounted(() => {
    if (isValid())
      return load()

    // Wait for transcription to load
    watchOnce(() => isValid(), load)
  })

  return { activeTrack }
}

function useCaptionsEditor(audioId: string, transcription: Ref<Transcription.IContent>) {
  const canEdit = import.meta.client ? !!useAudioFolders().getItem(audioId) : false
  const isEnabled = ref(false)

  const updateServer = debounce(500, () => {
    const content = sanitizeObject(transcription.value).map(i => ({
      ...i,
      sentences: i.sentences.map((j) => {
        const { id, ...rest } = j
        return rest
      })
    }))

    http.post(`/update-transcription/${audioId}`, content)
    track('Updated transcription', { audioId, content })
  })

  function update(cueId: number, text: string) {
    // Update cue text in transcription
    for (const item of transcription.value) {
      for (const sentence of item.sentences) {
        if (sentence.id === cueId) {
          sentence.text = text
          break
        }
      }
    }

    // Trigger update
    updateServer()
  }

  async function setSpeakerName(data: Transcription.IContentParagraph) {
    const previousName = transcription.value[data.index]?.speaker || ''
    const name = prompt('Enter new name for speaker', previousName)

    if (!previousName)
      throw new Error('Could not find previous name to update')

    if (!name)
      return alert('Speaker name cannot be empty. Please try again.')

    if (name.length > 25)
      return alert('Speaker name is too long. Please try again with a shorter name.')

    for (const paragraph of transcription.value) {
      if (paragraph.speaker === previousName)
        paragraph.speaker = name
    }

    track('Listen page: updated speaker name')
    await updateServer()
  }

  /** Toggles captions editor */
  function toggle() {
    isEnabled.value = !isEnabled.value
  }

  return toReactive({ canEdit, isEnabled, update, setSpeakerName, toggle })
}

export function useCaptions() {
  const source = useRecordingState()
  const state = reactiveComputed(() => {
    const data = source.data.value
    const rawTranscription = data?.audio.transcription || []
    const transcription = Transcription.fromRawContent(rawTranscription)

    return {
      transcription,

      /** Whether to show transcriptions */
      isEnabled: !!data?.settings.transcriptions && transcription.length > 0,

      /** Whether to show transcriptions */
      isParagraphed: transcription.some(i => i.sentences.length > 1),

      /** Whether transcription has speakers. Only available from Google Meet */
      hasSpeakers: transcription.some(entry => !!entry.speaker),

      /** Status of transcription */
      status: { [data?.audio.transcriptionStatus || 'loading']: true } as Transcription.IStatus
    }
  })

  const stateRefs = toRefs(state)
  const utils = useCaptionsLoader(stateRefs.transcription)
  const editor = useCaptionsEditor(source.data.value!.audio.id, stateRefs.transcription)

  /** Loads transcription data on client side */
  const refreshTranscription = async () => {
    if (!state.status.loading)
      return

    console.debug('Refreshing transcription status')
    await source.refresh()
    setTimeout(refreshTranscription, 5000)
  }

  onBeforeMount(() => {
    refreshTranscription()
  })

  return { ...utils, ...stateRefs, editor }
}

export function useSummary() {
  const data = getData()
  const value = data.audio.summary

  const isValid = !!value && data.audio.duration >= 120
  const isActive = ref(false)

  return toReactive({ value, isValid, isActive })
}
